// T137 — named filter views, persisted per-page in localStorage. No backend:
// single-operator host, instant frontend deploy. Mirrors the defensive
// try/catch discipline of hooks/useTheme.js.
//
// View shape: { id, name, query, isDefault, createdAt }
// where `query` is the canonical param-map from useUrlFilters.serializeFilters
// (e.g. { q:'react', src:'', country:'DK', score:'high', ai:'' }) so it
// round-trips through the URL identically.

// T290b — "single-operator host" above stopped being true: three accounts share
// this instance, and localStorage is per-BROWSER, so one person's saved views
// were the default for whoever signed in next on the same machine.
//
// Namespaced by user id now. Two deliberate choices about the existing data:
//   - adoption COPIES, it never moves. The legacy key is left exactly where it
//     is, so a mistake here cannot cost the operator views he named by hand.
//   - exactly one account adopts them, recorded by a marker. Without it every
//     account would inherit the same set and the leak would survive the fix.
const LEGACY_KEY = (ns) => `hunter-views:${ns}`;
const ADOPTED_KEY = (ns) => `hunter-views:${ns}:adopted`;

let _userNs = '';

/** Called by AuthProvider whenever the signed-in identity changes. */
export function setViewsUser(userId) {
  _userNs = userId ? `:${userId}` : '';
  if (userId) adoptLegacyViews();
}

const KEY = (ns) => `hunter-views:${ns}${_userNs}`; // ns: 'dashboard' | 'applications'

function adoptLegacyViews() {
  for (const ns of ['dashboard', 'applications']) {
    try {
      if (localStorage.getItem(ADOPTED_KEY(ns))) continue;
      if (localStorage.getItem(KEY(ns))) continue; // this account already has its own
      const legacy = localStorage.getItem(LEGACY_KEY(ns));
      if (!legacy) continue;
      localStorage.setItem(KEY(ns), legacy);
      localStorage.setItem(ADOPTED_KEY(ns), '1');
    } catch {
      /* quota / private mode — the views just start empty for this account */
    }
  }
}

function uid() {
  try {
    return crypto.randomUUID();
  } catch {
    return `v_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
  }
}

export function listViews(ns) {
  try {
    const v = JSON.parse(localStorage.getItem(KEY(ns)) || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function write(ns, views) {
  try {
    localStorage.setItem(KEY(ns), JSON.stringify(views));
  } catch {
    /* quota / private mode — silently no-op; the in-memory list still works */
  }
}

// Re-using a name overwrites (name is the upsert key) so the operator can't
// accumulate duplicates of the same view.
export function saveView(ns, name, query) {
  const all = listViews(ns);
  // T290 — an upsert must carry the flags the row already had. Re-saving a view
  // under its own name — the ordinary way to update one after changing a filter
  // — built a fresh object with `isDefault: false`, so the ★ silently came off
  // and the view stopped loading on arrival. The operator's only clue was that
  // the dashboard opened unfiltered the next morning.
  const prior = all.find((v) => v.name === name);
  const views = all.filter((v) => v.name !== name);
  const view = {
    id: prior?.id ?? uid(),
    name,
    query,
    isDefault: prior?.isDefault ?? false,
    createdAt: prior?.createdAt ?? Date.now(),
  };
  write(ns, [...views, view]);
  return view;
}

export function deleteView(ns, id) {
  write(ns, listViews(ns).filter((v) => v.id !== id));
}

export function setDefaultView(ns, id) {
  write(ns, listViews(ns).map((v) => ({ ...v, isDefault: v.id === id })));
}

export function getDefaultView(ns) {
  return listViews(ns).find((v) => v.isDefault) || null;
}
